import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardTitle, Grid, MetricCard, Pill, Btn, Insight, EmptyState } from '../components/UI';
import { supabase } from '../supabaseClient';

const F = 'system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif';

function useIsMobile() {
  const [mob, setMob] = React.useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  React.useEffect(() => {
    const h = () => setMob(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return mob;
}

const FIELD_LABEL = {
  source_content: 'Cited page changed',
  source_url:     'Source unreachable',
  last_audited:   'Overdue a re-check',
};
const FIELD_TYPE = { source_content: 'amber', source_url: 'red', last_audited: 'blue' };

const TRACK_LABEL = { stay: 'Live there', own: 'Own property', belong: 'A passport' };
const CONF_TYPE   = { verified: 'green', reported: 'amber' };
const CONF_LABEL  = {
  verified: 'Verified · statute or government source',
  reported: 'Reported · reputable secondary source',
};

function money(n, ccy) {
  if (n == null) return null;
  const v = Number(n);
  return (Number.isFinite(v) ? v.toLocaleString() : String(n)) + (ccy ? ' ' + ccy : '');
}

function when(ts) {
  if (!ts) return '—';
  const d = new Date(ts);
  return isNaN(d) ? '—' : d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

function Figure({ n, label }) {
  return (
    <div>
      <div style={{ fontSize: 22, fontWeight: 600, color: '#16303f', lineHeight: 1 }}>{n}</div>
      <div style={{ fontSize: 10.5, letterSpacing: 1.2, textTransform: 'uppercase', color: '#7b8b94', marginTop: 4 }}>{label}</div>
    </div>
  );
}

export default function Pathways() {
  const mob = useIsMobile();
  const [deltas, setDeltas] = useState(null);
  const [awaiting, setAwaiting] = useState(null);
  const [notes, setNotes]   = useState({});    // pathway_key -> reviewer note in progress
  const [runs, setRuns]     = useState([]);
  const [busy, setBusy]     = useState(null);
  const [err, setErr]       = useState('');

  // The route runs on the service key and mutates published legal content, so
  // it requires a signed-in admin. Every call carries the session token.
  const authHeaders = useCallback(async () => {
    const { data } = await supabase.auth.getSession();
    const token = data && data.session && data.session.access_token;
    return token ? { Authorization: 'Bearer ' + token } : {};
  }, []);

  const load = useCallback(async () => {
    setErr('');
    try {
      const r = await fetch('/api/pathways-review', { headers: await authHeaders() });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || 'load failed');
      setDeltas(j.deltas || []);
      setAwaiting(j.awaiting || []);
      setRuns(j.runs || []);
    } catch (e) {
      setErr(e.message);
      setDeltas([]);
      setAwaiting([]);
    }
  }, [authHeaders]);

  useEffect(() => { load(); }, [load]);

  async function act(id, action) {
    if (busy) return;   // Btn takes no `disabled` prop — guard re-entry here
    setBusy(id + action);
    try {
      const r = await fetch('/api/pathways-review', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
        body: JSON.stringify({ id, action }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || 'action failed');
      setDeltas((prev) => (prev || []).filter((d) => d.id !== id));
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(null);
    }
  }

  // Publish / reject a never-shown row. Addressed by pathway_key, not delta id.
  async function decide(pathwayKey, action) {
    if (busy) return;
    setBusy(pathwayKey + action);
    setErr('');
    try {
      const r = await fetch('/api/pathways-review', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(await authHeaders()) },
        body: JSON.stringify({ pathway_key: pathwayKey, action, note: notes[pathwayKey] || '' }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || 'action failed');
      setAwaiting((prev) => (prev || []).filter((a) => a.pathway_key !== pathwayKey));
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(null);
    }
  }

  const lastRun = runs[0];
  const waiting = awaiting ? awaiting.length : null;
  const open = deltas ? deltas.length : null;
  const unreachable = deltas ? deltas.filter((d) => d.field === 'source_url').length : null;
  const overdue = deltas ? deltas.filter((d) => d.field === 'last_audited').length : null;

  return (
    <div style={{ fontFamily: F }}>
      <Grid cols={4}>
        <MetricCard label="Awaiting first review" value={waiting === null ? '…' : String(waiting)}
          sub="researched, never shown" accent="#314F5C" />
        <MetricCard label="Drift to review" value={open === null ? '…' : String(open)}
          sub="published rows whose source moved" accent="#6F8FA7" />
        <MetricCard label="Sources unreachable" value={unreachable === null ? '…' : String(unreachable)}
          sub="citation no longer resolves" accent="#c0504a" />
        <MetricCard label="Overdue a re-check" value={overdue === null ? '…' : String(overdue)}
          sub="past review interval" accent="#9F7E4F" />
        <MetricCard label="Last harvest" value={lastRun ? when(lastRun.started_at) : '—'}
          sub={lastRun ? (lastRun.note || '') : 'never run'} accent="#6F8FA7" />
      </Grid>

      <Insight type="info">
        <b>A new row waits here until you have read it and its source.</b> Nothing you see in
        this list has ever been shown to a traveller. <b>Publish</b> means you stand behind the
        figure; phones that are online show it within seconds. <b>Reject</b> keeps it hidden with your
        note attached so the researcher knows why. Rows marked <i>reported</i> cite a reputable
        secondary source rather than the statute itself, and the app says so on the card.
      </Insight>

      <Card>
        <CardTitle action={<Btn variant="outline" small onClick={load}>Refresh</Btn>}>
          Awaiting first review
        </CardTitle>

        {awaiting === null ? (
          <div style={{ padding: 18, color: '#6b7a83', fontSize: 13 }}>Loading…</div>
        ) : awaiting.length === 0 ? (
          <EmptyState
            title="Nothing new"
            body="Every researched row has been read. New countries appear here as they are sourced."
            icon="✓"
          />
        ) : (
          awaiting.map((a) => (
            <div key={a.pathway_key} style={{ borderTop: '1px solid #eef2f5', padding: '18px 2px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 16, fontWeight: 600, color: '#16303f' }}>{a.country || a.iso3}</span>
                <Pill type="teal">{TRACK_LABEL[a.track] || a.track}</Pill>
                <Pill type={a.status === 'open' ? 'green' : a.status === 'closed' ? 'red' : 'amber'}>{a.status}</Pill>
                <Pill type={CONF_TYPE[a.confidence] || 'red'}>{CONF_LABEL[a.confidence] || a.confidence}</Pill>
              </div>

              <div style={{ fontSize: 14.5, fontWeight: 600, color: '#16303f', marginTop: 10 }}>{a.pathway_name}</div>
              {a.summary ? <div style={{ fontSize: 13.5, color: '#2f4552', marginTop: 6, lineHeight: 1.6 }}>{a.summary}</div> : null}
              {a.caveat ? <div style={{ fontSize: 13, color: '#6b5b45', marginTop: 8, lineHeight: 1.6,
                                        padding: '8px 12px', background: 'rgba(159,126,79,0.08)',
                                        borderLeft: '2px solid #9F7E4F', borderRadius: 6 }}>{a.caveat}</div> : null}

              <div style={{ display: 'flex', gap: 22, flexWrap: 'wrap', marginTop: 12 }}>
                {a.clock_years != null ? <Figure n={a.clock_years} label="years" /> : null}
                {a.quota_pct != null ? <Figure n={a.quota_pct + '%'} label="foreign cap" /> : null}
                {a.min_investment != null ? <Figure n={money(a.min_investment, a.investment_ccy)} label="threshold" /> : null}
                {a.presence_required != null ? <Figure n={a.presence_required ? 'yes' : 'no'} label="presence required" /> : null}
              </div>

              <div style={{ fontSize: 12, color: '#7b8b94', marginTop: 12, lineHeight: 1.8 }}>
                <code style={{ fontSize: 11.5, color: '#314F5C' }}>{a.pathway_key}</code>
                {' · as at ' + (a.as_at || '—')}
                {a.effective_from ? ' · in force ' + a.effective_from : ''}
                {' · re-check every ' + (a.review_interval_days || 120) + 'd'}
                {a.researched_by ? ' · researched by ' + a.researched_by : ''}
                <br />
                {a.source_url ? (
                  <a href={a.source_url} target="_blank" rel="noreferrer" style={{ color: '#314F5C', fontWeight: 600 }}>
                    Open the source ↗
                  </a>
                ) : <span style={{ color: '#c0504a' }}>No source URL — cannot publish</span>}
                {a.source_type ? <span>{'  ·  ' + a.source_type}</span> : null}
              </div>
              {Array.isArray(a.tr_missing) ? (
                a.tr_missing.length === 0 ? (
                  <div style={{ fontSize: 12.5, color: '#2f6b4f', marginTop: 8 }}>
                    ✓ Translated into all 15 app languages
                  </div>
                ) : (
                  <div style={{ fontSize: 12.5, color: '#8a5a12', marginTop: 8, lineHeight: 1.6,
                                padding: '8px 12px', background: 'rgba(214,160,60,0.10)', borderRadius: 6 }}>
                    <b>Not yet translated into {a.tr_missing.join(', ')}.</b> Publishing is blocked until these
                    are done, because a published row with gaps stops the next app release.{' '}
                    Run <code style={{ fontSize: 11.5 }}>npm run pathways:translate -- --pathway {a.pathway_key} --only {a.tr_missing.join(',')}</code>
                  </div>
                )
              ) : null}
              {a.research_note ? (
                <div style={{ fontSize: 12.5, color: '#3f5a68', marginTop: 8, lineHeight: 1.6, fontStyle: 'italic' }}>
                  Researcher’s note: {a.research_note}
                </div>
              ) : null}

              <div style={{ display: mob ? 'block' : 'flex', gap: 10, alignItems: 'center', marginTop: 14 }}>
                <input
                  value={notes[a.pathway_key] || ''}
                  onChange={(e) => setNotes((n) => ({ ...n, [a.pathway_key]: e.target.value }))}
                  placeholder="Note for the record (optional; required to reject)"
                  style={{ flex: 1, minWidth: 0, width: mob ? '100%' : undefined, padding: '9px 12px',
                           border: '1px solid #d7dee3', borderRadius: 8, fontSize: 13, fontFamily: F,
                           marginBottom: mob ? 10 : 0 }}
                />
                <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                  <Btn small variant="green" disabled={(a.tr_missing || []).length > 0}
                    onClick={() => { if ((a.tr_missing || []).length) return; decide(a.pathway_key, 'publish'); }}>
                    {busy === a.pathway_key + 'publish' ? 'Publishing…' : 'Publish'}
                  </Btn>
                  <Btn small variant="danger" onClick={() => {
                    if (!(notes[a.pathway_key] || '').trim()) { setErr('Say why you are rejecting it - the researcher needs the reason.'); return; }
                    decide(a.pathway_key, 'reject');
                  }}>
                    {busy === a.pathway_key + 'reject' ? 'Saving…' : 'Reject'}
                  </Btn>
                </div>
              </div>
            </div>
          ))
        )}
      </Card>

      <Insight type="info">
        The harvester never edits a published figure. It notices that a cited page moved, or
        that a rule is past its review interval, and asks a person to look. Approving re-stamps
        the review date; it does not change the number. Changing a figure stays a deliberate
        database edit with a source and a date behind it.
      </Insight>

      {err ? <Insight type="warn">{err}</Insight> : null}

      <Card>
        <CardTitle>Drift on published rows</CardTitle>

        {deltas === null ? (
          <div style={{ padding: 18, color: '#6b7a83', fontSize: 13 }}>Loading…</div>
        ) : deltas.length === 0 ? (
          <EmptyState
            title="Nothing waiting"
            body="Every published pathway has a source that still resolves and a review date inside its interval."
            icon="✓"
          />
        ) : (
          deltas.map((d) => {
            const p = d.pathway || {};
            return (
              <div key={d.id} style={{
                borderTop: '1px solid #eef2f5', padding: '16px 2px',
                display: mob ? 'block' : 'flex', gap: 18, alignItems: 'flex-start',
              }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 15, fontWeight: 600, color: '#16303f' }}>
                      {p.country || p.iso3 || d.pathway_key}
                    </span>
                    {p.track ? <Pill type="teal">{p.track}</Pill> : null}
                    <Pill type={FIELD_TYPE[d.field] || 'blue'}>
                      {FIELD_LABEL[d.field] || d.field}
                    </Pill>
                    {p.show_pathway === false ? <Pill type="red">withheld</Pill> : null}
                  </div>

                  <div style={{ fontSize: 13, color: '#3f5a68', marginTop: 6, lineHeight: 1.55 }}>
                    {d.reason}
                  </div>

                  <div style={{ fontSize: 12, color: '#7b8b94', marginTop: 8, lineHeight: 1.7 }}>
                    <code style={{ fontSize: 11.5, color: '#314F5C' }}>{d.pathway_key}</code>
                    {p.pathway_name ? ' · ' + p.pathway_name : ''}
                    {p.clock_years != null ? ' · ' + p.clock_years + ' yr published' : ''}
                    {p.quota_pct != null ? ' · ' + p.quota_pct + '% cap' : ''}
                    <br />
                    Last audited {p.last_audited || 'never'}
                    {p.review_interval_days ? ' · interval ' + p.review_interval_days + 'd' : ''}
                    {' · opened ' + when(d.created_at)}
                  </div>

                  {d.source_url ? (
                    <a href={d.source_url} target="_blank" rel="noreferrer"
                      style={{ fontSize: 12, color: '#314F5C', display: 'inline-block', marginTop: 8 }}>
                      Open the cited source ↗
                    </a>
                  ) : null}
                </div>

                <div style={{
                  display: 'flex', gap: 8, flexWrap: 'wrap',
                  marginTop: mob ? 12 : 0, flexShrink: 0,
                }}>
                  <Btn small variant="green" onClick={() => act(d.id, 'approve')}>
                    {busy === d.id + 'approve' ? 'Saving…' : 'Still holds'}
                  </Btn>
                  <Btn small variant="outline" onClick={() => act(d.id, 'dismiss')}>
                    {busy === d.id + 'dismiss' ? 'Saving…' : 'Dismiss'}
                  </Btn>
                  <Btn small variant="danger" onClick={() => act(d.id, 'withhold')}>
                    {busy === d.id + 'withhold' ? 'Saving…' : 'Withhold'}
                  </Btn>
                </div>
              </div>
            );
          })
        )}
      </Card>

      <Card>
        <CardTitle>Recent harvests</CardTitle>
        {runs.length === 0 ? (
          <div style={{ padding: 14, fontSize: 13, color: '#6b7a83' }}>
            No harvest has run yet. The cron fires Mondays at 05:00 UTC.
          </div>
        ) : runs.map((r) => (
          <div key={r.id} style={{
            borderTop: '1px solid #eef2f5', padding: '11px 2px',
            display: 'flex', justifyContent: 'space-between', gap: 14, flexWrap: 'wrap',
          }}>
            <div style={{ fontSize: 13, color: '#16303f' }}>
              {when(r.started_at)}
              <span style={{ color: '#7b8b94' }}>
                {'  ·  ' + (r.pathways || 0) + ' pathway(s), ' + (r.countries || 0) + ' country/countries'}
              </span>
            </div>
            <div style={{ fontSize: 12, color: '#7b8b94' }}>
              {r.note || (r.finished_at ? 'finished' : 'running…')}
              {r.proposed ? '  ·  ' + r.proposed + ' opened' : ''}
            </div>
          </div>
        ))}
      </Card>
    </div>
  );
}
